import {
    Sparkles, Bot, BookOpenText, FileSearch, Workflow, BarChart3, Cpu,
    MessageSquare, Users, Compass, Wrench, Hammer, Server, Gauge,
} from "lucide-react";
import PageMeta from "../components/layout/PageMeta";
import PageHero from "../components/sections/PageHero";
import FinalCTA from "../components/sections/FinalCTA";

const services = [
    { icon: <Sparkles className="w-5 h-5" />, title: "Generative AI Development", copy: "Domain-tuned LLM applications with RAG, structured generation and evaluation harnesses — engineered to ship, not to demo." },
    { icon: <Bot className="w-5 h-5" />, title: "Agentic AI Workflows", copy: "Tool-using agents that plan, act and respect human-in-the-loop checkpoints — wired into your CRMs, ITSMs, ERPs and internal APIs." },
    { icon: <BookOpenText className="w-5 h-5" />, title: "Enterprise AI Copilots", copy: "Role-specific copilots for HR, sales, support, finance and operations — grounded in your knowledge, secured by your policies." },
    { icon: <MessageSquare className="w-5 h-5" />, title: "Conversational AI & Chatbots", copy: "Multilingual, guardrailed interfaces backed by retrieval and analytics that close the feedback loop on every conversation." },
    { icon: <FileSearch className="w-5 h-5" />, title: "Document Intelligence", copy: "Extract, classify, summarise and route contracts, claims, invoices and unstructured archives — with audit-grade trails." },
    { icon: <Users className="w-5 h-5" />, title: "Resume & Talent Intelligence", copy: "Production-grade resume parsing, scoring and matching — the same engine that powers our TalentIQX accelerator." },
    { icon: <BarChart3 className="w-5 h-5" />, title: "Predictive & Decision Analytics", copy: "Forecasting, anomaly detection and decision support modelled on your business signals — explainable to your stakeholders." },
    { icon: <Cpu className="w-5 h-5" />, title: "MLOps & AI Deployment", copy: "Versioning, evaluation, monitoring and safe rollouts for the AI systems your business depends on every day." },
];

const useCases = [
    { icon: <BookOpenText className="w-4 h-4" />, title: "Knowledge assistants", metric: "−45% answer time", copy: "Conversational access to policies, SOPs and internal data — secure, audited and integrated with your identity provider." },
    { icon: <Users className="w-4 h-4" />, title: "Resume matching & screening", metric: "3.5× recruiter productivity", copy: "Score, rank and shortlist candidates against role context — at recruiter speed, with explainable reasoning." },
    { icon: <FileSearch className="w-4 h-4" />, title: "Document extraction", metric: "5× intake throughput", copy: "Pull structured fields and clauses from PDFs, scans and email attachments with confidence scoring." },
    { icon: <Workflow className="w-4 h-4" />, title: "Workflow automation agents", metric: "412 actions / day, automated", copy: "Agents that orchestrate multi-step approvals, lookups and updates across your tool estate." },
    { icon: <MessageSquare className="w-4 h-4" />, title: "Customer support copilots", metric: "+22% first-contact resolution", copy: "Suggested replies, summaries and resolution guidance for human agents — supervised end to end." },
    { icon: <Cpu className="w-4 h-4" />, title: "Internal operations automation", metric: "1,200+ hrs / month reclaimed", copy: "Reduce hand-offs across HR, finance, compliance and IT service workflows with policy-aware agents." },
];

const approach = [
    { step: "01", title: "Discover", copy: "Map outcomes, data and constraints. Identify the highest-ROI AI surfaces — and what to skip.", icon: <Compass className="w-4 h-4" /> },
    { step: "02", title: "Prototype", copy: "Rapid, evaluable prototypes with realistic data — never slideware demos.", icon: <Wrench className="w-4 h-4" /> },
    { step: "03", title: "Build", copy: "Production-grade implementation, governance and integration with your stack.", icon: <Hammer className="w-4 h-4" /> },
    { step: "04", title: "Deploy", copy: "Safe rollout with monitoring, evaluation and rollback paths in place.", icon: <Server className="w-4 h-4" /> },
    { step: "05", title: "Optimise", copy: "Iterate on prompts, retrieval, models and UX based on measured outcomes.", icon: <Gauge className="w-4 h-4" /> },
];

export default function AISolutions() {
    return (
        <div className="pqx-page-enter">
            <PageMeta
                title="AI Solutions"
                description="Outcome-led AI — generative, agentic, document intelligence and MLOps — designed to live inside your business and prove ROI from week one."
            />

            <PageHero
                eyebrow="AI Solutions"
                title="AI that earns its place"
                titleAccent="inside your business."
                description="From prototype to production — Perceptiqx delivers AI systems grounded in your data, integrated with your tools and accountable to measurable outcomes. No demo theatre."
                primaryCta={{ to: "/contact", label: "Map my AI roadmap (14 days)" }}
                secondaryCta={{ to: "/products", label: "See AI accelerators" }}
                bullets={["Generative AI", "Agentic workflows", "Document intelligence", "MLOps & evaluation"]}
            />

            {/* Services */}
            <section className="pqx-section bg-[#FAFAF8]" data-testid="ai-services">
                <div className="pqx-container">
                    <div className="max-w-3xl">
                        <span className="pqx-eyebrow">Services</span>
                        <h2 className="pqx-h2 mt-4 text-[#303030]">
                            A complete AI delivery practice.
                        </h2>
                        <p className="mt-5 text-[#303030]/70 text-base md:text-lg leading-relaxed max-w-2xl">
                            Specialist teams across LLM engineering, data, MLOps and product
                            design — composed into pods sized to your problem, not a service menu.
                        </p>
                    </div>

                    <div className="mt-12 md:mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
                        {services.map((s, i) => (
                            <div key={s.title} data-testid={`ai-service-${i}`} className="pqx-card-elevated !p-6 group">
                                <span className="w-11 h-11 grid place-items-center rounded-xl bg-[#303030]/5 text-[#303030] group-hover:bg-[#FF4040] group-hover:text-white transition-colors">
                                    {s.icon}
                                </span>
                                <h3 className="pqx-display text-lg text-[#303030] mt-5">
                                    {s.title}
                                </h3>
                                <p className="mt-3 text-sm text-[#303030]/70 leading-relaxed">
                                    {s.copy}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Use cases with metrics */}
            <section className="pqx-section bg-white" data-testid="ai-usecases">
                <div className="pqx-container">
                    <div className="max-w-3xl mb-12">
                        <span className="pqx-eyebrow">Use cases · with the numbers</span>
                        <h2 className="pqx-h2 mt-4 text-[#303030]">
                            Outcomes we ship every quarter.
                        </h2>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                        {useCases.map((u, i) => (
                            <div key={u.title} data-testid={`ai-usecase-${i}`} className="relative pqx-card-elevated !p-7 overflow-hidden">
                                <span className="absolute top-6 right-6 font-mono text-[11px] text-[#303030]/35">
                                    UC-{(i + 1).toString().padStart(2, "0")}
                                </span>
                                <span className="w-10 h-10 grid place-items-center rounded-xl bg-[#FF4040]/10 text-[#FF4040]">
                                    {u.icon}
                                </span>
                                <h3 className="pqx-display text-xl text-[#303030] mt-5">
                                    {u.title}
                                </h3>
                                <p className="mt-2 text-[12px] uppercase tracking-[0.16em] text-[#FF4040] font-semibold">
                                    {u.metric}
                                </p>
                                <p className="mt-3 text-sm text-[#303030]/70 leading-relaxed">
                                    {u.copy}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Approach */}
            <section className="pqx-section pqx-dark-panel relative overflow-hidden" data-testid="ai-approach">
                <div className="absolute inset-0 pqx-grid-bg-dark opacity-40" />
                <div className="pqx-container relative z-10">
                    <div className="max-w-3xl">
                        <span className="pqx-eyebrow !text-[#FF4040]">Our approach</span>
                        <h2 className="pqx-h2 mt-4 text-white">
                            Five disciplined steps. Zero pilots that go nowhere.
                        </h2>
                    </div>

                    <div className="mt-12 md:mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        {approach.map((a) => (
                            <div key={a.step} className="pqx-glass-dark rounded-2xl p-6 flex flex-col">
                                <div className="flex items-center justify-between">
                                    <span className="pqx-stat-num text-3xl text-[#FF4040]">{a.step}</span>
                                    <span className="w-8 h-8 grid place-items-center rounded-lg bg-white/10">
                                        {a.icon}
                                    </span>
                                </div>
                                <h3 className="pqx-display text-lg mt-6 text-white">{a.title}</h3>
                                <p className="mt-2 text-sm text-white/65 leading-relaxed">{a.copy}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <FinalCTA
                title="Get your AI roadmap in 14 days."
                description="We'll deliver a sequenced, ROI-mapped AI program shaped around your business — with the first prototype already in your hands."
                primary={{ to: "/contact", label: "Book a discovery call" }}
                secondary={{ to: "/products", label: "See accelerators" }}
            />
        </div>
    );
}
